import { createClient } from '@/lib/supabase';

const supabase = createClient();
const NOTIFICATIONS_TABLE = 'notifications';

export const NOTIFICATION_BREACH_SIDES = {
    above: 'above_limit',
    below: 'below_limit',
};

const toPositionKey = (value) => (typeof value === 'string' ? value.trim().toUpperCase() : '');

export const mapThresholdDirectionToBreachSide = (direction) => (
    direction === 'below' ? NOTIFICATION_BREACH_SIDES.below : NOTIFICATION_BREACH_SIDES.above
);

export const buildNotificationDedupeKey = ({ bridge, position, breachSide }) => {
    const bridgeKey = bridge || 'unknown';
    const positionKey = toPositionKey(position);
    const sideKey = breachSide || NOTIFICATION_BREACH_SIDES.above;
    return `${bridgeKey}|${positionKey}|${sideKey}`;
};

export const buildResolveAllPayload = (date = new Date()) => ({
    status: 'resolved',
    resolved_at: date instanceof Date ? date.toISOString() : new Date(date).toISOString(),
    read: true,
});

export const buildAutoResolvePayload = (date = new Date()) => ({
    status: 'resolved',
    resolved_at: date instanceof Date ? date.toISOString() : new Date(date).toISOString(),
    read: false,
});

export const buildActiveNotificationPayload = (payload = {}, date = new Date()) => {
    const timestamp = date instanceof Date ? date.toISOString() : new Date(date).toISOString();
    const bridge = payload.bridge || payload.bridgeName || 'unknown';
    const position = payload.position || payload.sensorPosition || payload.positionKey || '';
    const breachSide = payload.breach_side || mapThresholdDirectionToBreachSide(payload.direction);

    return {
        bridge,
        position,
        breach_side: breachSide,
        value: payload.value ?? null,
        threshold: payload.threshold ?? null,
        status: 'active',
        message: payload.message || 'Sensor threshold exceeded',
        details: payload.details || '',
        serial_number: payload.serial_number || payload.serialNumber || null,
        dedupe_key: buildNotificationDedupeKey({ bridge, position, breachSide }),
        read: false,
        updated_at: timestamp,
        resolved_at: null,
    };
};

export async function fetchNotifications() {
    const { data, error } = await supabase
        .from(NOTIFICATIONS_TABLE)
        .select('*')
        .order('updated_at', { ascending: false });
    if (error) throw error;
    return data || [];
}

export async function markNotificationRead(id) {
    const { error } = await supabase
        .from(NOTIFICATIONS_TABLE)
        .update({ read: true })
        .eq('id', id);
    if (error) throw error;
}

export async function markNotificationUnread(id) {
    const { error } = await supabase
        .from(NOTIFICATIONS_TABLE)
        .update({ read: false })
        .eq('id', id);
    if (error) throw error;
}

export async function markAllNotificationsRead() {
    const { error } = await supabase
        .from(NOTIFICATIONS_TABLE)
        .update({ read: true })
        .eq('status', 'active');
    if (error) throw error;
}

export async function resolveAllActiveNotifications(date = new Date()) {
    const { error } = await supabase
        .from(NOTIFICATIONS_TABLE)
        .update(buildResolveAllPayload(date))
        .eq('status', 'active');
    if (error) throw error;
}

export async function upsertActiveNotification(payload) {
    const nextPayload = buildActiveNotificationPayload(payload);
    const { data, error } = await supabase
        .from(NOTIFICATIONS_TABLE)
        .upsert(nextPayload, { onConflict: 'dedupe_key' })
        .select('*')
        .single();

    if (error) throw error;
    return data;
}

export async function resolveNotificationByDedupeKey({
    dedupe_key,
    bridge,
    position,
    breach_side,
    direction,
}, date = new Date()) {
    const dedupeKey = dedupe_key || buildNotificationDedupeKey({
        bridge,
        position,
        breachSide: breach_side || mapThresholdDirectionToBreachSide(direction),
    });

    const { data, error } = await supabase
        .from(NOTIFICATIONS_TABLE)
        .update(buildAutoResolvePayload(date))
        .eq('dedupe_key', dedupeKey)
        .eq('status', 'active')
        .select('*')
        .maybeSingle();

    if (error) throw error;
    return data || null;
}
